import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { AppBarWithButtons } from '../../../../../components/designe/app-bars/app-bar-with-action-buttons/AppBarWithButtons';
import AppFormSelectWrapper from '../../../../../components/designe/forms/app-form-select-wrapper';
import AppFormTextField from '../../../../../components/designe/forms/app-form-text-field';
import AppDialog from '../../../../../components/reusable/app-dialog/AppDialog';
import { useScrollToFirstError } from '../../../../../hooks/use-scroll-to-first-error';
import { getSelectableUnitsForParameter } from '../../../../../shared/constants/user-measurements';
import {
  IUserMeasurement,
  MeasurementParameter,
} from '../../../../../types/interfaces/user-measurement';
import { useStyles } from './RecordMeasurementModal.styles';
import { useRecordMeasurementModal } from './useRecordMeasurementModal';

interface IRecordMeasurementModalProps {
  open: boolean;
  parameter: MeasurementParameter;
  latestMeasurement: IUserMeasurement | undefined;
  onClose: () => void;
  onSuccess: () => void;
}

export default function RecordMeasurementModal({
  open,
  parameter,
  latestMeasurement,
  onClose,
  onSuccess,
}: IRecordMeasurementModalProps) {
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { t } = useTranslation();
  const { formik, isLoading } = useRecordMeasurementModal({
    parameter,
    latestMeasurement,
    onSuccess,
  });

  const unitOptions = useMemo(
    () =>
      getSelectableUnitsForParameter(parameter).map((unitKey) => ({
        id: unitKey,
        name: t(`settings.measurements.units.${unitKey}`),
      })),
    [parameter, t],
  );

  const title =
    parameter === 'weight'
      ? t('settings.measurements.modalTitleWeight')
      : t('settings.measurements.modalTitleHeight');

  useScrollToFirstError(formik, {
    fieldOrder: ['value', 'unitKey'],
  });

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    onClose();
  };

  return (
    <AppDialog
      classes={{ root: classes.recordMeasurementDialog }}
      open={open}
      onClose={handleClose}
      title={title}>
      <form
        className={classes.recordMeasurementForm}
        onSubmit={formik.handleSubmit}>
        <Box display="flex" flexDirection="column" gap={1}>
          <AppFormTextField
            fullWidth
            autoFocus
            type="number"
            name="value"
            formik={formik}
            mode="dark"
          />
          <AppFormSelectWrapper
            options={unitOptions}
            name="unitKey"
            formik={formik}
            disabled
            disableClear
            disableSearch
            disableSorting
            mode="dark"
          />
        </Box>
        <Box className={classes.buttonWrapper}>
          <AppBarWithButtons
            onCancelClick={handleClose}
            loading={isLoading}
            cancelDisabled={isLoading}
          />
        </Box>
      </form>
    </AppDialog>
  );
}
